import { getRecordReducer, LoadingMessage } from '@stormwater-software/lib-ui';
import { find, get, has } from 'lodash';
import React, {
  PropsWithChildren,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { ToastContainer } from 'react-toastify';
import SuccessNotification from '../components/Shared/SuccessNotification/SuccessNotification';
import userApi from '../utils/api/user';
import xhrService from '../utils/xhrService';
// import { MatomoProvider, createInstance } from '@datapunt/matomo-tracker-react';
import { setOfflineTenantScope } from '../db/db';
import {
  checkSession,
  deactivationOf,
  isUnreachable,
  loadSavedSession,
  hasSavedSession,
  resumePendingWipe,
  saveSession,
} from '../utils/offlineSession';
import { endSession } from '../components/Shared/OfflineSessionGate/SessionEndedDialog';

type AppContextValue = {
  state: any;
  initialDataLoaded: boolean;
  set(...args: any[]): any;
  get<V = any>(key: string): V;
  has(key: string): boolean;
  reloadInitialData(): void;
  triggerSuccessNotification(): void;

  getNotificationSetting(notificationType: string): {
    default_email_message?: string;
  };
  loadData<D = any>(cb: () => Promise<D>, what?: string): Promise<D>;
  isLoading: boolean;
  loadingResource: string;
  panel: string;
  setPanel: Function,
};
const AppContext = React.createContext<AppContextValue>(undefined as any);

export function AppStore(props: PropsWithChildren<{}>) {
  const [state, dispatch] = useReducer(getRecordReducer(), {});
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingResource, setLoadingResource] = useState('resource');
  const getPanelVal = localStorage.getItem('navigationPanel');
  const [panel, setPanel] = useState(
    getPanelVal === null ? 'open' : getPanelVal,
  );

  // const matomoInstance = createInstance({
  //   urlBase: 'https://sw2.matomo.cloud',
  //   siteId: 2,
  // });

  /**
   * load initial app data
   *
   * @todo catch errors, store in "globalErrors" key, display them
   */
  const set = (key: string, val: any) =>
    dispatch({
      type: 'mergeDeep',
      payload: { key, val },
    });

  const loadInitialData = async () => {
    // check if it is public url
    const path = window.location.pathname;
    const isPublic = path.startsWith('/public');
    // Pages a logged-out user is meant to be on (SW2-2474: never interrupted by
    // the session-ended dialog — a password reset link must open the reset form).
    const isAuthPage = ['/login', '/forgot', '/reset-password', '/user/register'].some(
      (route) => path.startsWith(route),
    );
    let checkForLoggedInData: any = {};

    if (isPublic && window.location.pathname.includes('/projects/')) {
      const projectId = Number(
        window.location.pathname.split('/projects/')[1].split('/')[0],
      );

      checkForLoggedInData.projectId = projectId;
    }
    // SW2-2474: with no connection, restore the session saved at the last
    // successful check instead of treating the user as logged out. Never on a
    // public page — see `saveSession`.
    const restoreOffline = async () => {
      const saved = isPublic ? null : await loadSavedSession();

      if (saved) applyProfile(saved, { unverified: true });

      return Boolean(saved);
    };

    // A deactivated user's delete that did not complete is finished before
    // anything is restored or uploaded.
    await resumePendingWipe();

    const session = await checkSession(checkForLoggedInData);

    if (session.state === 'loggedIn') {
      try {
        const appData = await xhrService
          .get('/api/users/profile')
          .then((res: any) => res.data);

        await saveSession(appData, { restorable: !isPublic });
        applyProfile(appData, { unverified: false });
      } catch (e) {
        // The connection dropped, or the server failed, between the two
        // requests: the same as never reaching it. Only an answer about the
        // session logs the user out.
        if (isUnreachable(e) && (await restoreOffline())) return;

        // A 401/403 here is the same answer as from isLoggedIn, and is handled
        // the same way (see below).
        if (!isUnreachable(e) && !isPublic && !isAuthPage) {
          const deactivation = deactivationOf(e);

          if (deactivation || (await hasSavedSession())) {
            await endSession(deactivation);

            return;
          }
        }

        userApi.logout();
        setInitialDataLoaded(true);
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }

      return;
    }

    if (session.state === 'unreachable' && (await restoreOffline())) return;

    // SW2-2474: tell the user why they were logged out. Deactivated: their
    // downloads go, unsynced work included (option 1). Otherwise only when this
    // device was signed in — a visitor never signed in just gets the login page
    // — and the work is kept for when they sign in again. Never on a public
    // page (its session is a visitor token, not this device's user), on the
    // sign-in and password pages, or when the cookie is for another tenant
    // (see SessionCheck): those go on as before.
    if (session.state === 'loggedOut' && !isPublic && !isAuthPage && !session.otherTenant) {
      if (session.deactivation || (await hasSavedSession())) {
        await endSession(session.deactivation);

        return;
      }
    }

    // nothing to load
    setInitialDataLoaded(true);
  };

  /**
   * Put a profile into the app state. `unverified` marks a session restored
   * offline: OfflineSessionGate checks it with the server once it can, and the
   * automatic sync waits until it has.
   */
  const applyProfile = (appData: any, { unverified }: { unverified: boolean }) => {
    const { appConstants, ...user } = appData;

    // Set before any offline read so tenant-scoped caches resolve to the
    // right tenant. Persisted on the singleton too: after an offline reload
    // this profile request cannot resolve, and without the stored copy every
    // tenant-scoped read would fail closed.
    setOfflineTenantScope(user.tenantId);

    dispatch({
      type: 'merge',
      payload: {
        user,
        isAdmin: user.roleName === 'Admin' || user.roleName === 'Super Admin',
        sessionUnverified: unverified,
        constants: {
          ...appConstants,
          nonDivisionRoutes: [
            '/divisions',
            '/inspection',
            '/user-manuals',
            '/public/inspection',
            '/administration',
            '/subscription',
          ],
          ccrTemplates: ['Delaware'],
          peTemplates: ['Delaware'],
        },
      },
    });
  };

  const loadData = async (cb: () => Promise<any>, what?: string) => {
    setLoadingResource(what ? what : '');
    setIsLoading(true);

    try {
      const data = await cb();
      setIsLoading(false);
      return data;
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const requiredProps = ['user', 'isAdmin', 'constants'];

    if (
      !initialDataLoaded &&
      requiredProps.every((prop) =>
        Object.prototype.hasOwnProperty.call(state, prop),
      )
    ) {
      setInitialDataLoaded(true);
    }
  }, [state, initialDataLoaded]);

  /**
   * Don't continue unless we have _required_ data
   */
  if (!initialDataLoaded) {
    return <LoadingMessage what='system data' />;
  }

  return (
    <AppContext.Provider
      value={{
        panel,
        setPanel,
        state,
        set,
        get: get.bind(null, state),
        has: (key) =>
          has(state, key) &&
          get(state, key) !== null &&
          get(state, key) !== undefined,

        reloadInitialData: () => loadInitialData(),
        triggerSuccessNotification: () => {
          const toId = window.setTimeout(() => {
            setShowSuccessNotification(false);
          }, 2000);

          setShowSuccessNotification(true);

          return toId;
        },
        getNotificationSetting: (notificationType) : any => {
          return find(get.bind(null, state)('constants.notificationSettings'), {
            name: notificationType,
          });
        },
        initialDataLoaded,
        loadData,
        loadingResource,
        isLoading
      }}
    >
      {' '}
      {props.children}
      {/* <MatomoProvider value={matomoInstance}>{props.children}</MatomoProvider> */}
      <SuccessNotification show={showSuccessNotification} />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        pauseOnFocusLoss={false}
        hideProgressBar
      />
    </AppContext.Provider>
  );
}

export default AppContext;
